import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {isOk} from '../../utils/apiResponse';
import {LocalStorage} from '../../utils/LocalStorage';
import {ScreenTracker} from '../../utils/ScreenTracker';
import {CLARITY_UNMASK} from '../../utils/clarity/unmask';
import {Spinner} from '../Spinner';
import {
    ORIGIN_APP, TRIAL_CODE_REDEEMED_EVENT, applyRedeemedTrial, dayAndMonth, fetchTrialCodeAvailability,
    redeemTrialCode, trialCodeErrorKey,
} from './trialCodeApi';
import './TrialCode.scss';

/**
 * Si a la agencia le sirve cargar un código hoy (`GET trial-code/`). `false` mientras pregunta, si falla o
 * si no corresponde: el acceso sólo se dibuja con un sí. Pregunta UNA vez por montaje, y sólo con `enabled`.
 * Un canje hecho en otra pantalla montada (el banner y la tarjeta de Suscripción conviven) lo apaga acá
 * también: si no, ofrecía un código que ya se había usado (review C-cy-t-03).
 */
export function useTrialCodeAvailability(enabled) {
    const [available, setAvailable] = useState(false);
    useEffect(() => {
        const onRedeemed = () => setAvailable(false);
        window.addEventListener(TRIAL_CODE_REDEEMED_EVENT, onRedeemed);
        return () => window.removeEventListener(TRIAL_CODE_REDEEMED_EVENT, onRedeemed);
    }, []);
    useEffect(() => {
        if (!enabled) {
            setAvailable(false);
            return undefined;
        }
        let alive = true;
        // Un «no» no toca el estado: ya arrancó en `false` (y así no hay un render de más en cada pantalla).
        fetchTrialCodeAvailability().then((answer) => {
            if (alive && answer) setAvailable(true);
        });
        return () => {
            alive = false;
        };
    }, [enabled]);
    return [available, setAvailable];
}

/** El campo y el botón. `onRedeemed(data)` recibe `{code, days, to_date, days_remaining}`. */
export function TrialCodeForm({origin = ORIGIN_APP, onRedeemed, autoFocus = false, idPrefix = 'trial-code'}) {
    const {t} = useTranslation();
    const [code, setCode] = useState(() => LocalStorage.getSignupTrialCode() || '');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const inputRef = useRef(null);
    const inputId = `${idPrefix}-input`;
    const errorId = `${idPrefix}-error`;

    useEffect(() => {
        if (autoFocus && inputRef.current) inputRef.current.focus();
    }, [autoFocus]);

    const handleSubmit = (event) => {
        event.preventDefault();
        const clean = code.trim();
        if (!clean || saving) return;
        setSaving(true);
        setError(null);
        redeemTrialCode(clean, origin).then((response) => {
            setSaving(false);
            if (isOk(response) && response.data) {
                applyRedeemedTrial(response.data);
                ScreenTracker.recordEvent('trial_code_redeemed');
                if (onRedeemed) onRedeemed(response.data);
                return;
            }
            setError(t(trialCodeErrorKey(response)));
            if (inputRef.current) inputRef.current.focus();
        });
    };

    return (
        <form className="trial-code-form" onSubmit={handleSubmit} noValidate>
            <label htmlFor={inputId} {...CLARITY_UNMASK}>{t('trial_code.label')}</label>
            <div className="trial-code-form__row">
                <input id={inputId} ref={inputRef} type="text" value={code} maxLength={40}
                       autoComplete="off" autoCapitalize="characters" spellCheck={false}
                       aria-invalid={error !== null} aria-describedby={error !== null ? errorId : undefined}
                       onChange={(event) => setCode(event.target.value)}/>
                {/* Mientras aplica, el botón es sólo el spinner: el nombre queda para el lector de pantalla. */}
                <button type="submit" className="btn -alt trial-code-form__submit" disabled={saving || !code.trim()}
                        aria-busy={saving} aria-label={saving ? t('trial_code.submit') : undefined} {...CLARITY_UNMASK}>
                    {saving ? <Spinner color="negative small"/> : t('trial_code.submit')}
                </button>
            </div>
            {error !== null && (
                <p className="trial-code-form__error" id={errorId} role="alert">{error}</p>
            )}
        </form>
    );
}

/**
 * Lo que se dice después de aplicarlo: la fecha y los días REALES de la prueba, no los del código. Con «nunca
 * acorta» (una prueba ya extendida a mano) «tu prueba ahora es de 30 días» contradecía la fecha (C-cy-t-09).
 */
export function TrialCodeSuccess({data}) {
    const {t} = useTranslation();
    // `escapeValue: false`: la fecha lleva «/», que i18next escribiría como `&#x2F;`.
    const options = {date: dayAndMonth(data.to_date), count: data.days_remaining, interpolation: {escapeValue: false}};
    return (
        <div className="trial-code-success" role="status">
            <p className="trial-code-success__title" {...CLARITY_UNMASK}>{t('trial_code.success_title')}</p>
            <p {...CLARITY_UNMASK}>
                {data.days_remaining === 0
                    ? t('trial_code.success_text_today', options)
                    : t('trial_code.success_text', options)}
            </p>
        </div>
    );
}

/**
 * La tarjeta que se muestra en una pantalla (Suscripción, la prueba vencida): no se dibuja si a la agencia no
 * le sirve un código. `after` es lo que va debajo del «Listo» (en la prueba vencida, el botón para volver).
 */
export function TrialCodeCard({onRedeemed, after = null, className = ''}) {
    const {t} = useTranslation();
    const [available] = useTrialCodeAvailability(LocalStorage.fetchUserType() === 'AGENCY_USER');
    const [redeemed, setRedeemed] = useState(null);

    if (!available && !redeemed) return null;

    const handleRedeemed = (data) => {
        setRedeemed(data);
        if (onRedeemed) onRedeemed(data);
    };

    return (
        <section className={`trial-code-card ${className}`.trim()} aria-labelledby="trial-code-card-title">
            {redeemed ? (
                <>
                    <TrialCodeSuccess data={redeemed}/>
                    {after}
                </>
            ) : (
                <>
                    <h2 id="trial-code-card-title" className="trial-code-card__title" {...CLARITY_UNMASK}>
                        {t('trial_code.card_title')}
                    </h2>
                    <p className="trial-code-card__text" {...CLARITY_UNMASK}>{t('trial_code.card_text')}</p>
                    <TrialCodeForm onRedeemed={handleRedeemed} idPrefix="trial-code-card"/>
                </>
            )}
        </section>
    );
}
